import type { ClientView, ScoreDelta } from '@gazette/shared';
import { listNames, t, tn } from '../../i18n/index.ts';
import { actions, isHost, playerById } from '../../net/client.ts';
import { ErrorNotice, GameHeader } from '../components/Chrome.tsx';
import { Clipping } from '../components/Clipping.tsx';
import { Icon } from '../components/Icon.tsx';
import { Portrait } from '../components/Portrait.tsx';
import type { PhaseOf } from '../hooks.ts';
import { CaseTabs } from './Investigation.tsx';

function reasonText(delta: ScoreDelta, fooled: number): string {
  return delta.reasons
    .map(({ reason }) => (reason === 'fooled' || reason === 'victim-bluff' ? `${t(`reason.${reason}`)} (${fooled})` : t(`reason.${reason}`)))
    .join(', ');
}

export function Reveal({ current, phase }: { current: ClientView; phase: PhaseOf<'reveal'> }) {
  const { verdict } = phase;
  const article = current.gazette!.articles.find((candidate) => candidate.id === phase.articleId)!;
  const victim = playerById(verdict.victimId);
  const author = playerById(verdict.authorId);
  const finders = verdict.unmaskedBy.map((id) => playerById(id)?.name ?? '?');
  const deltas = [...verdict.deltas].sort((a, b) => b.points - a.points);
  const last = phase.index + 1 >= phase.total;
  const nextLabel = !last ? t('rev.next') : phase.total >= 2 ? t('rev.toPlume') : t('rev.toResults');

  return (
    <>
      <GameHeader />
      <section class="sheet sheet--case" data-occlude aria-labelledby="verdict-title">
        <CaseTabs index={phase.index} total={phase.total} verdicts={current.verdicts} articleIds={current.gazette!.articles.map((entry) => entry.id)} />
        <Clipping article={article} victim={victim} size="small" />

        <div class="verdict" aria-live="polite">
          <h2 id="verdict-title" class={`verdict__stamp verdict__stamp--${verdict.isTrue ? 'true' : 'false'} reveal-step reveal-step--1`}>
            {t(verdict.isTrue ? 'rev.true' : 'rev.false')}
          </h2>
          <p class="verdict__fooled reveal-step reveal-step--1">
            {verdict.fooled.length === 0 ? t('rev.fooled.none') : tn('rev.fooled', verdict.fooled.length)}
          </p>
          {author && (
            <div class="verdict__author reveal-step reveal-step--2">
              <Portrait seed={author.portrait} width={64} />
              <p class="verdict__byline">{t('gz.signed', { name: author.name })}</p>
            </div>
          )}
          <p class="verdict__found reveal-step reveal-step--2">{finders.length ? t('rev.found', { names: listNames(finders) }) : t('rev.hidden')}</p>
        </div>

        <section class="points reveal-step reveal-step--3" aria-labelledby="points-title">
          <h3 id="points-title" class="section-title">
            {t('rev.points')}
          </h3>
          {deltas.length === 0 ? (
            <p class="hint">{t('rev.noPoints')}</p>
          ) : (
            <ul class="points__list">
              {deltas.map((delta) => {
                const player = playerById(delta.playerId);
                return (
                  <li class="points__row">
                    {player && <Portrait seed={player.portrait} width={30} />}
                    <span class="points__name">{player?.name}</span>
                    <span class="points__why">{reasonText(delta, verdict.fooled.length)}</span>
                    <span class="points__value">+{delta.points}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <div class="sheet__footer">
          <ErrorNotice />
          {isHost.value && (
            <button type="button" class="btn btn--primary btn--wide" onClick={() => void actions.advance()}>
              {nextLabel}
              <Icon name="next" size={20} />
            </button>
          )}
        </div>
      </section>
    </>
  );
}
